import { useMemo, useState } from 'react';
import { track } from '../lib/analytics';
import { useCurrency } from '../context/CurrencyContext';
import { useTracking } from '../context/TrackingContext';
import { useStoreContext } from '../context/StoreContext';

/**
 * Shared upsell ("Complete Your Kit") bundle block for cart drawers.
 *
 * Self-gated: renders nothing unless the universal `upsell_cart_enabled`
 * (mapped to `cart_upsell_enabled`) setting is on and there is a candidate
 * product to recommend. Mounted by the global cart drawer AND every premium
 * theme's cart drawer/popover so the toggle works on all themes.
 */
export default function CartUpsell() {
  const { cart, settings, addToCart, subdomain, products, collection_products } = useStoreContext() as any;
  const { trackAddToCart } = useTracking();
  const { convert } = useCurrency();

  const [selectedUpsellIds, setSelectedUpsellIds] = useState<string[]>([]);

  const relatedProducts = useMemo(() => {
    if (!settings?.cart_upsell_enabled) return [];
    if (!cart || cart.length === 0) return [];
    const lastItem = cart[cart.length - 1];
    const cartIds = new Set(cart.map((item: any) => item.id));

    const candidates = products.filter(
      (p: any) => p.id !== lastItem.id && !cartIds.has(p.id) && p.stock > 0
    );
    if (candidates.length === 0) return [];

    const lastItemCollectionIds = (collection_products || [])
      .filter((cp: any) => cp.product_id === lastItem.id)
      .map((cp: any) => cp.collection_id);

    const productCollectionSet: Record<string, Set<string>> = {};
    for (const cp of collection_products || []) {
      (productCollectionSet[cp.product_id] = productCollectionSet[cp.product_id] || new Set()).add(cp.collection_id);
    }

    const collectionMatches: any[] = [];
    if (lastItemCollectionIds.length > 0) {
      const collIdsSet = new Set(lastItemCollectionIds);
      collectionMatches.push(
        ...candidates.filter((p: any) =>
          (productCollectionSet[p.id] || new Set()).size > 0
            ? Array.from(productCollectionSet[p.id]).some((id) => collIdsSet.has(id as any))
            : false
        )
      );
    }

    const lastItemTags = (lastItem.tags || []).map((t: string) => t.toLowerCase());
    const tagMatches: any[] = [];
    if (lastItemTags.length > 0) {
      const lastItemTagsSet = new Set(lastItemTags);
      tagMatches.push(
        ...candidates.filter((p: any) =>
          (p.tags || []).some((t: string) => lastItemTagsSet.has(t.toLowerCase()))
        )
      );
    }

    const recommendedList: any[] = [];
    const recommendedIds = new Set<string>();
    const addUnique = (items: any[]) => {
      for (const item of items) {
        if (!recommendedIds.has(item.id)) {
          recommendedList.push(item);
          recommendedIds.add(item.id);
          if (recommendedList.length >= 2) return true;
        }
      }
      return false;
    };

    if (addUnique(collectionMatches)) return recommendedList;
    if (addUnique(tagMatches)) return recommendedList;
    addUnique(candidates);
    return recommendedList.slice(0, 2);
  }, [cart, products, collection_products, settings?.cart_upsell_enabled]);

  if (relatedProducts.length === 0) return null;

  const lastCartItem = cart[cart.length - 1];
  const selectedProducts = relatedProducts.filter((p: any) => selectedUpsellIds.includes(p.id));

  const totalUpsellPrice = selectedProducts.reduce((sum: number, p: any) => sum + p.price, 0);
  const totalUpsellComparePrice = selectedProducts.reduce(
    (sum: number, p: any) => sum + (p.compare_price || p.price),
    0
  );

  const bundlePrice = lastCartItem.price + totalUpsellPrice;
  const bundleComparePrice = (lastCartItem.compare_price || lastCartItem.price) + totalUpsellComparePrice;
  const totalItemsCount = 1 + selectedProducts.length;

  const handleAddBundle = () => {
    if (selectedProducts.length === 0) return;
    selectedProducts.forEach((p: any) => {
      addToCart(p, 1);
      trackAddToCart(p, 1);
      track(subdomain, 'add_to_cart', { productId: p.id });
    });
    setSelectedUpsellIds([]);
  };

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '16px',
        borderRadius: 'var(--product-card-radius, 12px)',
        background: 'var(--surface, #f8f9fa)',
        border: '1px solid var(--border, #e8e8e8)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <span>⚡</span> Complete Your Kit
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', padding: '4px 0' }}>
        <div style={{ position: 'relative', width: '54px', height: '54px', borderRadius: '8px', border: '1.5px solid var(--accent, #fd3c02)', overflow: 'hidden', flexShrink: 0 }}>
          <img src={lastCartItem.images?.[0] || lastCartItem.image} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
          <div style={{ position: 'absolute', bottom: '2px', right: '2px', background: 'var(--accent, #fd3c02)', color: 'var(--accent-contrast, #ffffff)', borderRadius: '50%', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 'bold' }}>✓</div>
        </div>

        {relatedProducts.map((p: any) => {
          const isSelected = selectedUpsellIds.includes(p.id);
          const toggleSelect = () => {
            setSelectedUpsellIds((prev: string[]) =>
              prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]
            );
          };
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-muted, #888)' }}>+</div>
              <div
                onClick={toggleSelect}
                style={{
                  position: 'relative',
                  width: '54px',
                  height: '54px',
                  borderRadius: '8px',
                  border: isSelected ? '1.5px solid var(--accent, #fd3c02)' : '1.5px solid var(--border, #e8e8e8)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: isSelected ? '0 4px 8px rgba(0,0,0,0.05)' : 'none',
                }}
              >
                <img src={p.images?.[0] || p.image} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isSelected ? 1 : 0.6 }} alt="" />
                <div
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: '2px',
                    background: isSelected ? 'var(--accent, #fd3c02)' : 'rgba(255,255,255,0.8)',
                    border: isSelected ? 'none' : '1px solid var(--border, #e8e8e8)',
                    borderRadius: '4px',
                    width: '12px',
                    height: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  {isSelected && <span style={{ color: 'var(--accent-contrast, #ffffff)', fontSize: '8px', fontWeight: 'bold' }}>✓</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {relatedProducts.map((p: any) => {
          const isSelected = selectedUpsellIds.includes(p.id);
          const toggleSelect = () => {
            setSelectedUpsellIds((prev: string[]) =>
              prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]
            );
          };
          return (
            <div
              key={p.id}
              onClick={toggleSelect}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                padding: '8px 10px',
                borderRadius: '8px',
                background: isSelected ? 'var(--accent-light, rgba(253,60,2,0.05))' : 'var(--surface-card, #ffffff)',
                border: '1px solid ' + (isSelected ? 'var(--accent, #fd3c02)' : 'var(--border, #e8e8e8)'),
                transition: 'all 0.2s ease',
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={toggleSelect}
                style={{ accentColor: 'var(--accent, #fd3c02)', cursor: 'pointer' }}
                onClick={(e) => e.stopPropagation()}
              />
              <div style={{ flex: 1, fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', flexShrink: 0 }}>
                <span style={{ fontWeight: 700, color: 'var(--accent, #fd3c02)' }}>{convert(p.price)}</span>
                {p.compare_price && p.compare_price > p.price && (
                  <span style={{ textDecoration: 'line-through', color: 'var(--text-muted, #888)', fontSize: '10px', opacity: 0.6 }}>
                    {convert(p.compare_price)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed var(--border, #e8e8e8)', paddingTop: '10px', marginTop: '4px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-muted, #888)', fontWeight: 600 }}>
            {totalItemsCount} items selected
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: 800 }}>{convert(bundlePrice)}</span>
            {bundleComparePrice > bundlePrice && (
              <span style={{ fontSize: '11px', textDecoration: 'line-through', color: 'var(--text-muted, #888)' }}>
                {convert(bundleComparePrice)}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleAddBundle}
          disabled={selectedProducts.length === 0}
          style={{
            backgroundColor: selectedProducts.length > 0 ? 'var(--accent, #fd3c02)' : 'var(--border, #e8e8e8)',
            color: selectedProducts.length > 0 ? 'var(--accent-contrast, white)' : 'var(--text-muted, #888)',
            border: 'none',
            padding: '10px 16px',
            borderRadius: 'var(--button-radius, 8px)',
            fontSize: '12px',
            fontWeight: 700,
            cursor: selectedProducts.length > 0 ? 'pointer' : 'not-allowed',
            boxShadow: selectedProducts.length > 0 ? '0 4px 12px rgba(253,60,2,0.15)' : 'none',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            if (selectedProducts.length > 0) e.currentTarget.style.filter = 'brightness(1.1)';
          }}
          onMouseLeave={(e) => {
            if (selectedProducts.length > 0) e.currentTarget.style.filter = 'brightness(1)';
          }}
        >
          Add Bundle
        </button>
      </div>
    </div>
  );
}